// Composition de seance : chaque seance de l'ecran Construction est un
// corps de seance, une seance complete, un echauffement, une recuperation
// ou un modele, ou une competition. Une seance complete et une competition
// se posent telles quelles dans le calendrier. Un
// modele encadre un corps de seance par un echauffement et une
// recuperation ; le modele par defaut de l'utilisateur (preference) est
// applique a chaque corps de seance pose dans le calendrier (cote serveur,
// cf PlanningService.composer).
import * as React from 'react';

import { type Seance } from './seancesApi.ts';

export type CodeCategorie = 'CORPS' | 'SEANCE' | 'COMPETITION' | 'ECHAUFFEMENT' | 'RECUPERATION' | 'MODELE';

export type Categorie = { code: CodeCategorie; libelle: string; description: string };

export const CATEGORIES: Categorie[] = [
  { code: 'CORPS', libelle: 'Corps de séance', description: 'Partie principale, posée dans le calendrier avec ton modèle de répétitions ou de sortie continue' },
  { code: 'SEANCE', libelle: 'Séance', description: 'Séance complète, posée telle quelle dans le calendrier (sans modèle)' },
  { code: 'COMPETITION', libelle: 'Compétition', description: 'Course (objectif ou préparation), posée telle quelle dans le calendrier' },
  { code: 'ECHAUFFEMENT', libelle: 'Échauffement', description: 'Début de séance, utilisé par les modèles' },
  { code: 'RECUPERATION', libelle: 'Récupération', description: 'Fin de séance (retour au calme), utilisée par les modèles' },
  { code: 'MODELE', libelle: 'Modèle', description: 'Échauffement + corps de séance + récupération' },
];

export function categorie(code?: string | null): Categorie {
  return CATEGORIES.find((c) => c.code === code) ?? CATEGORIES[0];
}

/** Categorie d'une seance (CORPS pour les seances enregistrees avant la composition). */
export function categorieDe(seance: { categorie?: string | null }): CodeCategorie {
  return categorie(seance.categorie).code;
}

/** Se pose dans le calendrier (corps de seance, seance complete ou competition). */
export function estPlanifiable(code: CodeCategorie): boolean {
  return code === 'CORPS' || code === 'SEANCE' || code === 'COMPETITION';
}

/** A un type d'entrainement (court, seuil...) : corps de seance et seance complete. */
export function aUnType(code: CodeCategorie): boolean {
  return code === 'CORPS' || code === 'SEANCE';
}

/** Notation complete : parties non vides jointes par " + ". */
export function composerNotation(...parties: (string | null | undefined)[]): string {
  return parties.map((p) => (p ?? '').trim()).filter(Boolean).join(' + ');
}

export type PartiesModele = { echauffement?: Seance; recuperation?: Seance };

/** Seances d'echauffement et de recuperation d'un modele, retrouvees dans la liste. */
export function partiesModele(modele: Seance | null | undefined, seances: Seance[]): PartiesModele {
  if (!modele) return {};
  return {
    echauffement: seances.find((s) => s.id && s.id === modele.idEchauffement),
    recuperation: seances.find((s) => s.id && s.id === modele.idRecuperation),
  };
}

/** Synthese d'un modele (somme de ses parties), pour la liste "Mes seances". */
export function syntheseModele(parties: PartiesModele): Partial<Seance> {
  const liste = [parties.echauffement, parties.recuperation].filter((s): s is Seance => !!s);
  const somme = (cle: 'dureeTotaleS' | 'distanceTotaleM' | 'chargeUA') =>
    liste.length === 0 || liste.some((s) => s[cle] === null || s[cle] === undefined)
      ? null
      : liste.reduce((t, s) => t + (s[cle] as number), 0);
  return { dureeTotaleS: somme('dureeTotaleS'), distanceTotaleM: somme('distanceTotaleM'), chargeUA: somme('chargeUA') };
}

export type TotauxSeance = { dureeTotaleS: number | null; distanceTotaleM: number | null; chargeUA: number | null };

/**
 * Totaux d'un corps de seance une fois compose avec son modele
 * (echauffement + corps + recuperation) : meme somme que
 * PlanningService.composer cote serveur, sur les syntheses calculees a
 * l'enregistrement par le moteur (seanceEngine). null sans modele ou si le
 * modele n'a pas de partie ; une valeur est null si une partie ne la
 * connait pas. Hors semaine d'assimilation (modele dedie).
 */
export function totauxAvecModele(
  corps: Seance,
  modele: Seance | null | undefined,
  seances: Seance[],
): TotauxSeance | null {
  const parties = partiesModele(modele, seances);
  if (!parties.echauffement && !parties.recuperation) return null;
  const synthese = syntheseModele(parties);
  const somme = (cle: keyof TotauxSeance) => {
    const valeurCorps = corps[cle];
    const valeurParties = synthese[cle];
    return valeurCorps == null || valeurParties == null ? null : valeurCorps + valeurParties;
  };
  return { dureeTotaleS: somme('dureeTotaleS'), distanceTotaleM: somme('distanceTotaleM'), chargeUA: somme('chargeUA') };
}

/** Description lisible d'un modele, ex: "Échauffement 20' + [corps] + Retour 10'". */
export function descriptionModele(parties: PartiesModele): string {
  return [parties.echauffement?.nom, '[corps de séance]', parties.recuperation?.nom].filter(Boolean).join(' + ');
}

/* =================================================================
   MODELES PAR DEFAUT (preference : /api/preference/modele-seance)
   ================================================================= */

/**
 * Modele applique a un corps de seance : celui des seances de repetitions
 * (fractionne, seuil, pyramide, combinee), celui des sorties longues /
 * tempo continu, ou aucun. Meme regle que UsageModele cote serveur.
 */
export type UsageModele = 'REPETITIONS' | 'CONTINU' | 'AUCUN';

export type InfoUsage = { code: Exclude<UsageModele, 'AUCUN'>; libelle: string; description: string };

export const USAGES_MODELE: InfoUsage[] = [
  { code: 'REPETITIONS', libelle: 'Répétitions', description: 'Fractionné court, moyen, long, seuil, pyramide, combinée' },
  { code: 'CONTINU', libelle: 'Sortie longue / continu', description: 'Tempo continu, endurance' },
];

/**
 * Modeles par defaut des preferences : les deux usages, plus celui des
 * repetitions en semaine d'assimilation ou d'affutage (applique par la
 * semaine, pas un choix possible sur une seance).
 */
export type UsageParDefaut = Exclude<UsageModele, 'AUCUN'> | 'ASSIMILATION';
export type InfoUsageParDefaut = { code: UsageParDefaut; libelle: string; description: string };

export const USAGES_PAR_DEFAUT: InfoUsageParDefaut[] = [
  ...USAGES_MODELE,
  {
    code: 'ASSIMILATION',
    libelle: 'Assimilation / affûtage',
    description: "Répétitions des semaines d'assimilation ou d'affûtage (à défaut : modèle répétitions)",
  },
];

export function libelleUsage(usage: UsageModele | UsageParDefaut): string {
  return usage === 'AUCUN' ? 'Aucun' : USAGES_PAR_DEFAUT.find((u) => u.code === usage)?.libelle ?? usage;
}

/** Choix automatique selon le type de seance (cf classification.ts). */
export function usagePourType(typeSeance?: string | null): UsageModele {
  return typeSeance === 'TEMPO' || typeSeance === 'ENDURANCE' ? 'CONTINU' : 'REPETITIONS';
}

/** Modele force sur la seance (REPETITIONS, CONTINU, AUCUN), sinon selon son type. */
export function usageDe(modeleSeance: string | null | undefined, typeSeance: string | null | undefined): UsageModele {
  return modeleSeance === 'REPETITIONS' || modeleSeance === 'CONTINU' || modeleSeance === 'AUCUN'
    ? modeleSeance
    : usagePourType(typeSeance);
}

export type ModelesParDefaut = {
  repetitions: Seance | null;
  continu: Seance | null;
  assimilation: Seance | null;
  // Calendrier d'un athlete : modeles par defaut du coach, appliques pour un
  // usage que l'athlete n'a pas, et parties (echauffements, recuperations)
  // de tous ces modeles, que l'athlete ne voit pas dans ses seances.
  parDefaut?: ModelesParDefaut | null;
  parties?: Seance[];
};

const AUCUN_MODELE: ModelesParDefaut = { repetitions: null, continu: null, assimilation: null };

/** Modele choisi dans les preferences pour un usage (sans repli). */
export function modeleDefini(modeles: ModelesParDefaut, usage: UsageParDefaut): Seance | null {
  return usage === 'CONTINU' ? modeles.continu : usage === 'ASSIMILATION' ? modeles.assimilation : modeles.repetitions;
}

/**
 * Modele a appliquer pour un usage : si un seul des deux modeles est
 * defini, il sert aussi pour l'autre usage (meme regle que le serveur).
 */
export function modelePour(modeles: ModelesParDefaut, usage: UsageModele): Seance | null {
  return usage === 'AUCUN' ? null : modeleApplique(modeles, usage);
}

function modeleApplicable(modeles: ModelesParDefaut, usage: UsageParDefaut): Seance | null {
  if (usage === 'ASSIMILATION') return modeles.assimilation ?? modeleApplicable(modeles, 'REPETITIONS');
  return usage === 'CONTINU' ? modeles.continu ?? modeles.repetitions : modeles.repetitions ?? modeles.continu;
}

/**
 * Modele applique pour un usage, y compris l'assimilation (a defaut celui
 * des repetitions) ; pour un athlete sans modele, celui du coach.
 */
export function modeleApplique(modeles: ModelesParDefaut, usage: UsageParDefaut): Seance | null {
  return modeleApplicable(modeles, usage) ?? (modeles.parDefaut ? modeleApplicable(modeles.parDefaut, usage) : null);
}

export const MODELE_PAR_DEFAUT_MODIFIE_EVENT = 'app:modele-seance-modifie';

function entetes(): HeadersInit {
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') };
}

const normaliserSeance = (x?: Seance | null) => (x ? { ...x, notation: x.notation ?? '' } : null);

function normaliserModeles(m: Partial<ModelesParDefaut>): ModelesParDefaut {
  return {
    repetitions: normaliserSeance(m.repetitions),
    continu: normaliserSeance(m.continu),
    assimilation: normaliserSeance(m.assimilation),
    parDefaut: m.parDefaut ? normaliserModeles(m.parDefaut) : null,
    parties: (m.parties ?? []).map((x) => normaliserSeance(x) as Seance),
  };
}

// Modeles de l'utilisateur, ou (coach) d'un athlete rattache (son id SSO).
function adresseModeles(athlete?: string | null): string {
  return '/api/preference/modele-seance' + (athlete ? '?athlete=' + encodeURIComponent(athlete) : '');
}

async function lireModeles(r: Response): Promise<ModelesParDefaut> {
  if (r.status === 200) {
    return normaliserModeles((await r.json()) as Partial<ModelesParDefaut>);
  }
  let message = 'statut ' + r.status;
  try {
    const erreur = await r.json();
    if (erreur?.message) message = erreur.message;
  } catch {
    // corps vide
  }
  throw new Error(message);
}

export async function obtenirModelesParDefaut(athlete?: string | null): Promise<ModelesParDefaut> {
  return lireModeles(await fetch(adresseModeles(athlete), { headers: entetes() }));
}

/** Choisit le modele d'un usage (null = aucun) et previent les ecrans qui l'utilisent. */
export async function definirModeleParDefaut(
  usage: UsageParDefaut,
  idModele: string | null,
  athlete?: string | null,
): Promise<ModelesParDefaut> {
  const modeles = await lireModeles(
    await fetch(adresseModeles(athlete), { method: 'PUT', headers: entetes(), body: JSON.stringify({ usage, idModele }) }),
  );
  window.dispatchEvent(new Event(MODELE_PAR_DEFAUT_MODIFIE_EVENT));
  return modeles;
}

/**
 * Modeles par defaut de l'utilisateur, ou (coach) d'un athlete rattache.
 * `rafraichissement` relance la lecture (ex: apres suppression d'un modele).
 */
export function useModelesParDefaut(rafraichissement = 0, athlete: string | null = null, actif = true): ModelesParDefaut {
  const [modeles, setModeles] = React.useState<ModelesParDefaut>(AUCUN_MODELE);
  React.useEffect(() => {
    if (!actif) return;
    const charger = () =>
      obtenirModelesParDefaut(athlete)
        .then(setModeles)
        .catch(() => { /* preference indisponible : pas de modele */ });
    charger();
    window.addEventListener(MODELE_PAR_DEFAUT_MODIFIE_EVENT, charger);
    return () => window.removeEventListener(MODELE_PAR_DEFAUT_MODIFIE_EVENT, charger);
  }, [rafraichissement, athlete, actif]);
  return modeles;
}

/** Usages pour lesquels la seance est le modele par defaut (pour l'afficher). */
export function usagesDuModele(id: string | undefined, modeles: ModelesParDefaut): UsageParDefaut[] {
  if (!id) return [];
  return USAGES_PAR_DEFAUT.filter((u) => modeleDefini(modeles, u.code)?.id === id).map((u) => u.code);
}
